import React, { FC } from 'react';
import moment from 'moment';
import { Price } from 'shared-types/Common';
import { SpaSettingsType } from 'shared-types/SpaPackage';

import { useTranslations } from '@lang';
import { Button } from '@common/components';
import { DiscountPrice } from '@common/components/Price';
import { analytics } from '@features/analytics';

import { SpaClassTimeSlot } from '../SpaClasses.types';

import { ClockIcon } from '@root/styles/icons';

import {
  StyledClass,
  ClassRow,
  ClassCol,
  LeadingText,
  DurationWrapper,
  NumberOfSpaces,
  ClassMessage,
} from './SingleClass.styles';

type SpaClassTimeSlotWithDiscount = SpaClassTimeSlot & {
  discount?: { discountedPrice: Price | null } | null;
};

type SingleClassProps = {
  timeslot: SpaClassTimeSlotWithDiscount;
  onBookClass: (timeslot: SpaClassTimeSlotWithDiscount) => void;
  spaSettings?: SpaSettingsType;
};

export const SingleClass: FC<SingleClassProps> = ({ onBookClass, timeslot, spaSettings }) => {
  const { t } = useTranslations();

  const {
    price,
    numberOfSpacesAvailable,
    isSuspended,
    startDateTime,
    endDateTime,
    staffName,
    gender,
    discount,
    spaClass,
  } = timeslot;

  const isError = !numberOfSpacesAvailable || isSuspended;

  const shouldShowQuantity = !spaSettings?.providerDisabledFeatures?.includes('spaClassQuantity');

  function onBookClick() {
    onBookClass(timeslot);

    const { spaClass } = timeslot;

    analytics.track({
      event: 'view_item',
      params: {
        currency: price.currencyCode,
        value: price.amount,
        items: [
          {
            item_category: 'spa-classes',
            item_id: `${spaClass?.externalId}`,
            item_name: spaClass?.name,
          },
        ],
      },
    });
  }

  return (
    <StyledClass isFull={!numberOfSpacesAvailable} isSuspended={isSuspended}>
      <ClassRow>
        <ClassCol>
          <div>
            <LeadingText>{moment(startDateTime).format('HH:mm')}</LeadingText>
            <DurationWrapper>
              <ClockIcon width="14" height="14" />
              {t('spaClasses.min', {
                duration: moment(endDateTime).diff(moment(startDateTime), 'minutes'),
              })}
            </DurationWrapper>
          </div>
        </ClassCol>
        <ClassCol isGrow>
          <div>
            <LeadingText>{spaClass?.name}</LeadingText>
            <div>
              {spaClass?.categories?.length > 0 &&
                `${spaClass.categories.map(({ name }) => name).join(' • ')} • `}
              {staffName && `${staffName}`}
            </div>
          </div>
        </ClassCol>
        <ClassCol align="flex-end">
          <LeadingText>
            <DiscountPrice price={price} discount={discount} />
          </LeadingText>
        </ClassCol>
      </ClassRow>

      <ClassRow>
        {shouldShowQuantity && (
          <ClassCol direction="row" align="flex-end" numberColumn>
            {numberOfSpacesAvailable > 0 && !isSuspended && (
              <NumberOfSpaces isError={numberOfSpacesAvailable <= 2}>
                {t('spaClasses.spaces', { number: numberOfSpacesAvailable })}
              </NumberOfSpaces>
            )}
          </ClassCol>
        )}
        <ClassCol isGrow direction="row" align="flex-end" isError={isError}>
          {numberOfSpacesAvailable > 0 && !isSuspended && (
            <>
              {gender === 'F' && <ClassMessage>{t('spaClasses.female')}</ClassMessage>}
              {gender === 'M' && <ClassMessage>{t('spaClasses.male')}</ClassMessage>}
            </>
          )}
          {!numberOfSpacesAvailable && (
            <ClassMessage isFull>{t('spaClasses.classFull')}</ClassMessage>
          )}
          {isSuspended && <ClassMessage isSuspended>{t('spaClasses.classSuspended')}</ClassMessage>}
        </ClassCol>
        <ClassCol align="flex-end">
          {numberOfSpacesAvailable > 0 && !isSuspended && (
            <Button
              isPrimary
              isSmall
              onClick={onBookClick}
              ariaLabel={`${t('spa.book')} - ${spaClass?.name}`}
            >
              {t('spa.book')}
            </Button>
          )}
        </ClassCol>
      </ClassRow>
    </StyledClass>
  );
};
